// The staff pages also run inside the GRYD portal's frame (its Webinars app,
// dashboard/app/webinars). The portal draws the rail and sidebar, so here the
// page drops its own top bar, tells the portal which page it is on (so the
// portal's address bar and back button follow), signs in through the portal
// without a click, and opens the studio, the one page that needs the whole
// window, in a tab of its own.

import { api } from './api'

export const embedded: boolean = (() => {
  try { return window.self !== window.top } catch { return true }
})()
if (embedded) document.documentElement.dataset.embedded = ''

let portalUrl: string | null = null
let portalOrigin: string | null = null
let pending: string | null = null
if (embedded) {
  api<{ signin: { portal: string | null } }>('/api/me').then(me => {
    try { portalOrigin = me.signin.portal ? new URL(me.signin.portal).origin : null } catch { portalOrigin = null }
    portalUrl = portalOrigin ? me.signin.portal : null
    if (pending) tellPortal(pending)
  }).catch(() => {})
}

/** The portal only hears it when it is the page framing us (targetOrigin). */
export function tellPortal(path: string): void {
  if (!embedded) return
  if (!portalOrigin) { pending = path; return }
  pending = null
  window.parent.postMessage({ type: 'gryd-live:route', path }, portalOrigin)
}

/**
 * The studio (or an attendee page) in a tab of its own. The frame's sign-in
 * stays in the frame, so the studio goes through the portal, which signs the
 * new tab in too. Called straight from the click, or popup blockers step in.
 */
export function openFull(path: string): void {
  window.open(portalUrl ? `${portalUrl}/webinars/go?to=${encodeURIComponent(path)}` : path, '_blank')
}

/**
 * Sign in inside the frame. The portal page is signed in and this frame is
 * not; the frame can't just visit the portal (a browser doesn't send the
 * portal's cookie on a navigation started inside a frame from another site),
 * so it asks the page, which answers with a one-minute token for /auth/portal.
 * Resolves false when no token comes.
 */
export function requestPortalSignIn(portal: string, returnTo: string): Promise<boolean> {
  let origin: string
  try { origin = new URL(portal).origin } catch { return Promise.resolve(false) }
  return new Promise(resolve => {
    const ask = () => window.parent.postMessage({ type: 'gryd-live:signin' }, origin)
    const done = (ok: boolean) => { window.removeEventListener('message', onMessage); clearTimeout(timer); clearInterval(again); resolve(ok) }
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== origin || e.source !== window.parent) return
      const d = e.data as { type?: unknown; token?: unknown } | null
      if (d?.type !== 'gryd-live:handoff') return
      if (typeof d.token !== 'string' || !d.token) return done(false)
      location.replace(`/auth/portal?t=${encodeURIComponent(d.token)}&return_to=${encodeURIComponent(returnTo)}`)
      done(true)
    }
    const timer = setTimeout(() => done(false), 10_000)
    window.addEventListener('message', onMessage)
    // Again until the page answers: it may not be listening yet.
    const again = setInterval(ask, 1000)
    ask()
  })
}

// A browser that refuses the frame's cookie comes back still signed out; the
// sign-in must not loop.
const TRIED = 'gl-portal-signin'
export function portalSignInJustFailed(): boolean {
  try { return Date.now() - Number(sessionStorage.getItem(TRIED) ?? 0) < 20_000 } catch { return true }
}
export function notePortalSignIn(): void {
  try { sessionStorage.setItem(TRIED, String(Date.now())) } catch { /* no storage: the check above says failed */ }
}
